import { MarketingLayout } from "@/components/layout/MarketingLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { SEO } from "@/components/SEO";
import {
  Calendar,
  Target,
  Mail,
  Users,
  TrendingUp,
  CheckCircle,
  Shield,
  Clock,
  Zap,
  BarChart3,
  MessageCircle,
  ArrowRight,
  Sparkles,
  Database,
  RefreshCw
} from "lucide-react";

const BOOKING_URL = "https://cal.com/scott-dudley-ftors8/15min";

export default function FeaturesPage() {
  return (
    <MarketingLayout>
      <SEO
        title="What We Deliver | Prozpector - Done-For-You SDR Service"
        description="Qualified meetings on your calendar. Personalized outreach at scale. Full pipeline visibility. We handle everything so you can focus on closing."
        url="https://prozpector.com/features"
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-gray-50 py-20">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">Done-For-You Outbound</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              Meetings Booked. Pipeline Filled. <br />
              <span className="text-primary">You Close.</span>
            </h1>
            <p className="text-xl text-gray-600 mb-10">
              We don't sell software. We deliver results. Our team uses intelligent
              systems to fill your calendar with qualified prospects while you
              focus on what you do best.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="text-lg px-8 py-6 gap-2">
                  <Calendar className="h-5 w-5" />
                  Book Discovery Call
                </Button>
              </a>
              <a href="https://cal.com/scott-dudley-ftors8/15min" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="text-lg px-8 py-6">
                  Get a Quote
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Core Outcomes */}
      <section className="py-20 bg-white border-b">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">What You Actually Get</h2>
            <p className="text-xl text-gray-600">Tangible results, not software features</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="p-8 text-center border-2 hover:border-primary transition-colors">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Calendar className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold mb-3">Meetings On Calendar</h3>
              <p className="text-gray-600">
                Qualified discovery calls booked directly on your calendar.
                Real prospects who match your ICP and are ready to talk.
              </p>
            </Card>

            <Card className="p-8 text-center border-2 hover:border-primary transition-colors">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Database className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold mb-3">Full Pipeline Visibility</h3>
              <p className="text-gray-600">
                See every prospect, every touchpoint, every response.
                Your own dashboard with complete transparency into the process.
              </p>
            </Card>

            <Card className="p-8 text-center border-2 hover:border-primary transition-colors">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold mb-3">Predictable Revenue</h3>
              <p className="text-gray-600">
                Consistent outbound activity means consistent pipeline.
                No more feast-or-famine lead flow.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* What We Handle */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                We Handle the Entire Outbound Process
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                From identifying prospects to booking the meeting, we manage every step
                so you can focus on delivery and closing.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Ideal Customer Research</h4>
                    <p className="text-gray-600">We identify and validate your perfect prospects</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Email & LinkedIn Outreach</h4>
                    <p className="text-gray-600">Personalized multi-channel sequences at scale</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Response Handling</h4>
                    <p className="text-gray-600">We manage replies and qualify interest</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Meeting Booking</h4>
                    <p className="text-gray-600">Qualified calls land directly on your calendar</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg">Continuous Optimization</h4>
                    <p className="text-gray-600">A/B testing and refinement to improve results</p>
                  </div>
                </div>
              </div>
            </div>

            <Card className="p-8 bg-white border-2">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold">Your Monthly Deliverables</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Target className="h-6 w-6 text-blue-600" />
                    <span className="font-medium">Prospects Identified</span>
                  </div>
                  <span className="text-xl font-bold text-blue-600">500+</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="h-6 w-6 text-purple-600" />
                    <span className="font-medium">Personalized Touches</span>
                  </div>
                  <span className="text-xl font-bold text-purple-600">2,000+</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <MessageCircle className="h-6 w-6 text-green-600" />
                    <span className="font-medium">Replies Managed</span>
                  </div>
                  <span className="text-xl font-bold text-green-600">All</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-green-100 rounded-lg border-2 border-green-500">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-6 w-6 text-green-700" />
                    <span className="font-bold text-green-700">Meetings Booked</span>
                  </div>
                  <span className="text-xl font-bold text-green-700">Your Goal</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Deep Personalization */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <Card className="p-8 bg-gradient-to-br from-purple-50 to-white order-2 md:order-1">
              <div className="space-y-4">
                <div className="font-mono text-sm bg-white p-4 rounded-lg border">
                  <p className="text-gray-600">
                    Hi <span className="bg-yellow-200 px-1 font-semibold">Sarah</span>,
                  </p>
                  <p className="text-gray-600 mt-2">
                    Saw <span className="bg-yellow-200 px-1 font-semibold">TechCorp</span> just
                    <span className="bg-yellow-200 px-1 font-semibold">raised Series B</span> — congrats!
                  </p>
                  <p className="text-gray-600 mt-2">
                    When we helped <span className="bg-blue-200 px-1 font-semibold">similar SaaS companies</span> at
                    this stage, they typically struggle with...
                  </p>
                </div>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                  <Sparkles className="h-4 w-4 text-purple-600" />
                  <span>Every message researched and personalized</span>
                </div>
              </div>
            </Card>

            <div className="order-1 md:order-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-purple-600" />
                </div>
                <h2 className="text-3xl font-bold">Deep Personalization</h2>
              </div>
              <p className="text-xl text-gray-600 mb-6">
                Every message is researched and personalized. No templates.
                No "Dear {'{firstName}'}" spam. Real context that gets replies.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Company Research</div>
                    <div className="text-gray-600">Recent news, funding, hiring signals</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Role-Specific Messaging</div>
                    <div className="text-gray-600">Different angles for different personas</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Trigger-Based Outreach</div>
                    <div className="text-gray-600">Reach out at the right moment</div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Multi-Channel Approach */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <RefreshCw className="h-6 w-6 text-blue-600" />
                </div>
                <h2 className="text-3xl font-bold">Multi-Channel Sequences</h2>
              </div>
              <p className="text-xl text-gray-600 mb-6">
                We don't rely on just one channel. Strategic combinations of
                email and LinkedIn maximize response rates.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Email Sequences</div>
                    <div className="text-gray-600">Perfectly timed follow-ups that don't feel automated</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">LinkedIn Outreach</div>
                    <div className="text-gray-600">Connection requests and thoughtful messages</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Smart Stopping</div>
                    <div className="text-gray-600">Sequences pause automatically when they reply</div>
                  </div>
                </li>
              </ul>
            </div>

            <Card className="p-8 bg-white">
              <div className="text-center mb-6">
                <h3 className="text-lg font-bold text-gray-600">Example Sequence</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-sm font-bold text-blue-600">1</div>
                  <div className="flex-1">
                    <div className="font-semibold">LinkedIn Connection</div>
                    <div className="text-sm text-gray-600">Day 0 - Personalized note</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-sm font-bold text-purple-600">2</div>
                  <div className="flex-1">
                    <div className="font-semibold">First Email</div>
                    <div className="text-sm text-gray-600">Day 2 - Research-backed opener</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-sm font-bold text-purple-600">3</div>
                  <div className="flex-1">
                    <div className="font-semibold">Follow-up Email</div>
                    <div className="text-sm text-gray-600">Day 5 - Value-add content</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-sm font-bold text-blue-600">4</div>
                  <div className="flex-1">
                    <div className="font-semibold">LinkedIn Message</div>
                    <div className="text-sm text-gray-600">Day 8 - Direct ask</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-sm font-bold text-green-600">5</div>
                  <div className="flex-1">
                    <div className="font-semibold">Break-up Email</div>
                    <div className="text-sm text-gray-600">Day 12 - Final touch</div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Full Transparency */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <Card className="p-8 bg-gradient-to-br from-indigo-50 to-white order-2 md:order-1">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white p-4 rounded-lg border text-center">
                    <div className="text-3xl font-bold text-blue-600">487</div>
                    <div className="text-sm text-gray-600 mt-1">Prospects Active</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border text-center">
                    <div className="text-3xl font-bold text-purple-600">2,341</div>
                    <div className="text-sm text-gray-600 mt-1">Emails Sent</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border text-center">
                    <div className="text-3xl font-bold text-green-600">23%</div>
                    <div className="text-sm text-gray-600 mt-1">Response Rate</div>
                  </div>
                  <div className="bg-white p-4 rounded-lg border text-center">
                    <div className="text-3xl font-bold text-orange-600">12</div>
                    <div className="text-sm text-gray-600 mt-1">Meetings This Month</div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold">Top Performing ICP</span>
                    <TrendingUp className="h-4 w-4 text-green-600" />
                  </div>
                  <div className="text-gray-600 text-sm">Series A SaaS - VP Marketing</div>
                  <div className="text-xl font-bold text-green-600 mt-1">31% response rate</div>
                </div>
              </div>
            </Card>

            <div className="order-1 md:order-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <BarChart3 className="h-6 w-6 text-indigo-600" />
                </div>
                <h2 className="text-3xl font-bold">Full Transparency Dashboard</h2>
              </div>
              <p className="text-xl text-gray-600 mb-6">
                Your own dashboard to see exactly what's happening.
                No black boxes. No mystery. Just results you can track.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Real-Time Pipeline</div>
                    <div className="text-gray-600">See every prospect and their status</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Activity Feed</div>
                    <div className="text-gray-600">Every email, reply, and interaction logged</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Performance Metrics</div>
                    <div className="text-gray-600">Response rates, meetings, and trends</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Message Preview</div>
                    <div className="text-gray-600">See exactly what we're sending</div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Different */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Not Another SDR Tool</h2>
            <p className="text-xl text-gray-600">The difference between software and service</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="p-8 border-2 border-red-200 bg-red-50/50">
              <h3 className="text-xl font-bold mb-4 text-red-800">SDR Software (DIY)</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-gray-700">
                  <span className="text-red-500 mt-1">✕</span>
                  <span>You still write the copy</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <span className="text-red-500 mt-1">✕</span>
                  <span>You still find the prospects</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <span className="text-red-500 mt-1">✕</span>
                  <span>You still manage the sequences</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <span className="text-red-500 mt-1">✕</span>
                  <span>You still handle replies</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <span className="text-red-500 mt-1">✕</span>
                  <span>Learning curve and setup time</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8 border-2 border-green-500 bg-green-50/50">
              <h3 className="text-xl font-bold mb-4 text-green-800">Prozpector (Done-For-You)</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>We write personalized copy</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>We find and verify prospects</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>We run the entire campaign</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>We qualify and book meetings</span>
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Live in days, not weeks</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-16 bg-white border-t">
        <div className="container">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            <div className="flex items-center gap-2 text-gray-600">
              <Shield className="h-5 w-5 text-green-600" />
              <span>GDPR Compliant</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Clock className="h-5 w-5 text-blue-600" />
              <span>Live in 48 Hours</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Zap className="h-5 w-5 text-purple-600" />
              <span>No Long-Term Contracts</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Users className="h-5 w-5 text-orange-600" />
              <span>Dedicated Account Manager</span>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-black text-white">
        <div className="container text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Fill Your Calendar?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Let's discuss your ideal customer profile and how we can
            start booking qualified meetings for your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="text-lg px-8 py-6 bg-white text-black hover:bg-gray-100 gap-2">
                <Calendar className="h-5 w-5" />
                Book Discovery Call
              </Button>
            </a>
            <a href="https://cal.com/scott-dudley-ftors8/15min" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white hover:text-black gap-2">
                Get a Quote
                <ArrowRight className="h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
